import { useState, createContext, useContext } from "react";
import { useAuth } from "../lib/AuthContext";
import { LogoIcon } from "./Icons";
import ContactForm from "./ContactForm";

// --- Sign-in prompt context (any component can trigger the sign-in modal) ---
interface SignInPromptContextType {
  promptSignIn: (reason?: string) => void;
}

const SignInPromptContext = createContext<SignInPromptContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useSignInPrompt() {
  const context = useContext(SignInPromptContext);
  if (!context) throw new Error("useSignInPrompt must be used within AuthGate");
  return context;
}

// --- Sub-components (unchanged) ---

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-bg max-w-2xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-light">
          <h2 className="text-lg font-semibold font-[family-name:--font-ui] text-text">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-muted hover:text-text">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto prose-tufte text-sm">
          {children}
        </div>
        <div className="p-4 border-t border-border-light">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-border text-text rounded-md font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 active:bg-black/10 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function TermsOfService({ onContactClick }: { onContactClick?: () => void }) {
  return (
    <>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">1. Acceptance of Terms</h3>
      <p>By accessing and using GlycemicAI (&ldquo;the Service&rdquo;), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service. These terms apply to all users, whether or not you create an account.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">2. Description of Service</h3>
      <p>GlycemicAI is an AI-powered tool that provides information about the glycemic index and glycemic load of foods, along with general nutrition guidance. The Service uses Google Gemini AI to analyze food items and provide estimated values.</p>
      <p className="mt-2">The Service offers two tiers of access:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Without an account:</strong> Limited free queries to try the search feature.</li>
        <li><strong>With a Google account:</strong> Unlimited searches (within daily limits), personalized recommendations, meal tracking, dashboard, and all features.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">3. Medical Disclaimer</h3>
      <p><strong>IMPORTANT:</strong> The information provided by GlycemicAI is for educational and informational purposes only. It is NOT intended as a substitute for professional medical advice, diagnosis, or treatment.</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li>Glycemic index and glycemic load values are AI-generated estimates and may vary based on food preparation, portion size, ripeness, and individual factors.</li>
        <li>If you have diabetes, prediabetes, or any other health condition, consult with your healthcare provider before making dietary changes.</li>
        <li>Do not disregard professional medical advice or delay seeking it because of information obtained from this Service.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">4. Limitation of Liability</h3>
      <p>GlycemicAI and its creators shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service, including but not limited to any health-related decisions made based on information provided.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">5. Accuracy of Information</h3>
      <p>While we strive to provide accurate information, we make no warranties or representations about the accuracy, reliability, or completeness of the glycemic index data or nutritional information provided. AI-generated responses may occasionally contain errors.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">6. User Responsibilities</h3>
      <p>You agree to:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li>Use the Service responsibly and for personal, non-commercial purposes.</li>
        <li>Not rely solely on this Service for managing health conditions.</li>
        <li>Consult healthcare professionals for medical advice.</li>
        <li>Not attempt to circumvent usage limits or rate limiting.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">7. Privacy</h3>
      <p>Your use of the Service is also governed by our Privacy Policy, which describes what data we collect and how we use it. By using the Service, you acknowledge that you have read and understood our Privacy Policy.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">8. Changes to Terms</h3>
      <p>We reserve the right to modify these terms at any time. Continued use of the Service after changes constitutes acceptance of the new terms.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">9. Contact</h3>
      <p>For questions about these Terms of Service, please {onContactClick ? <button type="button" onClick={onContactClick} className="text-accent hover:underline">contact us</button> : "contact us through our website"}.</p>
    </>
  );
}

function PrivacyPolicy({ onContactClick }: { onContactClick?: () => void }) {
  return (
    <>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">1. Information We Collect</h3>
      <p>GlycemicAI can be used without an account (limited free queries) or with a Google sign-in. The data we collect depends on how you use the Service:</p>

      <p className="font-[family-name:--font-ui] font-medium text-text mt-4 mb-1">All users (including without an account):</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Analytics Data:</strong> If you consent, we collect anonymized usage data via Google Firebase Analytics, including: pages and features visited, search queries made (text length and category, not content), session duration, device type, browser, language, and general location (country/region derived from IP address). Analytics data is associated with a random, anonymous identifier &mdash; not your identity.</li>
        <li><strong>Rate-Limiting Data:</strong> We temporarily process your IP address server-side to enforce usage limits for unauthenticated requests. IP addresses are held only in server memory and are not stored or logged.</li>
      </ul>

      <p className="font-[family-name:--font-ui] font-medium text-text mt-4 mb-1">Signed-in users:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Account Information:</strong> Your name, email address, and profile picture from your Google account.</li>
        <li><strong>Dietary Preferences:</strong> Diet type and food allergies or intolerances you provide to personalize recommendations.</li>
        <li><strong>Usage Statistics:</strong> Queries made, meals logged, photos analyzed, streak data, and badges earned &mdash; stored in your account.</li>
        <li><strong>Feedback Messages:</strong> If you submit feedback, we store your message, selected category, and optionally your email address (only if you provide it).</li>
        <li><strong>Analytics User Properties:</strong> If you consent to analytics, we also send the following to Google Analytics as user properties: diet type, whether you have allergies (yes/no), streak length, total query count, and badge count. These help us understand usage patterns in aggregate. Your email and name are never sent to analytics.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">2. How We Use Your Information</h3>
      <p>We use your information to:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li>Provide and improve the Service.</li>
        <li>Authenticate your identity (signed-in users).</li>
        <li>Respond to your queries about food and glycemic index.</li>
        <li>Personalize AI responses using your dietary preferences and allergies (sent to Google Gemini alongside your query).</li>
        <li>Track your usage statistics and progress.</li>
        <li>Enforce rate limits and prevent abuse.</li>
        <li>Analyze aggregated, anonymized usage patterns to improve features and user experience.</li>
        <li>Monitor app performance, measure feature effectiveness, and fix errors.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">3. Data Storage and Security</h3>
      <p>We use Firebase (Google Cloud) for authentication, data storage, and analytics.</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Server-side:</strong> Your account data, dietary preferences, usage statistics, and feedback messages are stored securely in Google Cloud Firestore and are accessible only to you (and app administrators for feedback).</li>
        <li><strong>Client-side (your device):</strong> We use your browser&apos;s local storage to remember your cookie consent choice, reminder preferences, daily energy log, glycemic load target, and anonymous query count. This data never leaves your device unless you sign in.</li>
      </ul>
      <p>We implement reasonable security measures to protect your information, but no method of transmission over the Internet is 100% secure.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">4. Third-Party Services</h3>
      <p>We use the following third-party services:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Google Firebase Authentication:</strong> For secure sign-in with your Google account.</li>
        <li><strong>Google Cloud Firestore:</strong> For storing your preferences, statistics, and feedback.</li>
        <li><strong>Google Firebase Analytics:</strong> For collecting anonymized usage data (only with your consent). This includes event tracking (e.g., feature usage, query counts, navigation patterns) and user properties (e.g., diet type, streak length) for signed-in users. No personally identifiable information (name, email) is sent to Analytics.</li>
        <li><strong>Google Gemini AI:</strong> For processing food-related queries and image analysis. Your text queries, uploaded images, and dietary preferences (if set) are sent to Google&apos;s AI services for processing. Google&apos;s AI data handling policies apply.</li>
      </ul>
      <p>These services have their own privacy policies. Learn more at <a href="https://policies.google.com/privacy" className="text-accent hover:underline">Google&apos;s Privacy Policy</a> and <a href="https://ai.google.dev/terms" className="text-accent hover:underline">Google AI Terms</a>.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">5. Cookies and Local Storage</h3>
      <p>We use browser local storage (not traditional cookies) to store your preferences and consent choices on your device. Firebase Analytics may set its own cookies for session tracking if you consent to analytics.</p>
      <p className="mt-2">Specifically, we store the following on your device:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li>Your cookie/analytics consent choice and timestamp</li>
        <li>Reminder and notification preferences</li>
        <li>Daily energy log and glycemic load target</li>
        <li>Anonymous query count (for unauthenticated rate limiting)</li>
      </ul>
      <p>You can clear this data at any time through your browser settings. You can also toggle analytics on or off in your Profile &gt; Privacy settings at any time.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">6. Data Retention</h3>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li><strong>Chat conversations:</strong> Processed in real-time by Google Gemini and not permanently stored on our servers.</li>
        <li><strong>Account data and preferences:</strong> Retained as long as you maintain an account. Deleted upon account deletion.</li>
        <li><strong>Feedback messages:</strong> Retained until resolved or for up to 24 months, then deleted.</li>
        <li><strong>Analytics data:</strong> Retained by Google Analytics for up to 14 months in aggregated, anonymized form.</li>
        <li><strong>Local storage data:</strong> Stored on your device until you clear your browser data.</li>
        <li><strong>Rate-limiting data:</strong> Held in server memory only; not persisted to any database. Cleared when the server restarts.</li>
      </ul>
      <p>You can update or delete your account data at any time from your Profile settings.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">7. Your Rights</h3>
      <p>You have the right to:</p>
      <ul className="list-disc pl-5 space-y-1 my-2">
        <li>Use the Service without creating an account (limited functionality).</li>
        <li>Decline analytics tracking &mdash; the Service works fully without it.</li>
        <li>Access the personal information we hold about you (via Profile &gt; Privacy &gt; Export Data).</li>
        <li>Update or correct your dietary preferences and profile information.</li>
        <li>Toggle analytics on or off at any time in Profile &gt; Privacy settings.</li>
        <li>Delete your account and all associated data (via Profile &gt; Privacy).</li>
        <li>Request additional data access or deletion by contacting us.</li>
      </ul>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">8. Children&apos;s Privacy</h3>
      <p>GlycemicAI is not intended for users under 13 years of age. We do not knowingly collect information from children.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">9. Changes to This Policy</h3>
      <p>We may update this Privacy Policy from time to time. We will notify users of significant changes through the Service. The &ldquo;last updated&rdquo; date at the top reflects the most recent revision.</p>

      <h3 className="font-[family-name:--font-ui] font-semibold text-text mt-6 mb-2">10. Contact Us</h3>
      <p>For questions about this Privacy Policy, please {onContactClick ? <button type="button" onClick={onContactClick} className="text-accent hover:underline">contact us</button> : "contact us through our website"}.</p>
    </>
  );
}

// --- Main AuthGate component ---

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading, signInWithGoogle, signOut, hasAcceptedTerms, acceptTerms } = useAuth();
  const [acceptedCheckboxes, setAcceptedCheckboxes] = useState({
    terms: false,
    health: false,
    privacy: false,
  });
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInReason, setSignInReason] = useState("Sign in to continue");

  const allCheckboxesAccepted =
    acceptedCheckboxes.terms && acceptedCheckboxes.health && acceptedCheckboxes.privacy;

  const handleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
      setShowSignInModal(false);
    } catch (error) {
      console.error("Sign in error:", error);
    } finally {
      setIsSigningIn(false);
    }
  };

  const promptSignIn = (reason?: string) => {
    setSignInReason(reason || "Sign in to continue");
    setShowSignInModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-[100dvh] bg-bg flex items-center justify-center">
        <div className="text-center">
          <LogoIcon className="w-12 h-12 text-accent mx-auto mb-4" />
          <div className="spinner mx-auto"></div>
        </div>
      </div>
    );
  }

  // Signed in but hasn't accepted terms — gate the app
  if (user && !hasAcceptedTerms) {
    return (
      <SignInPromptContext.Provider value={{ promptSignIn }}>
        <div className="min-h-[100dvh] bg-bg flex flex-col items-center p-5 py-8 overflow-y-auto">
          <div className="max-w-[440px] w-full my-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-6">
                <LogoIcon className="w-10 h-10 text-accent flex-shrink-0" />
                <div>
                  <h1 className="text-lg font-semibold text-text">Welcome, {user.displayName?.split(" ")[0]}.</h1>
                  <p className="text-sm text-muted font-[family-name:--font-ui]">Please review and accept before continuing.</p>
                </div>
              </div>

              <div className="section-label">Health Notice</div>
              <p className="text-sm text-text mb-6">
                GlycemicAI provides <strong>informational content only</strong> and is not a
                substitute for professional medical advice. Always consult a healthcare
                provider before making dietary changes, especially if you have diabetes or
                other health conditions.
              </p>

              <div className="border-t border-border-light pt-4 space-y-4 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedCheckboxes.health}
                    onChange={(e) => setAcceptedCheckboxes((prev) => ({ ...prev, health: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-accent"
                  />
                  <span className="text-sm text-text">
                    I understand that GlycemicAI is for <strong>informational purposes only</strong> and I will consult a healthcare professional before making any health-related decisions.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedCheckboxes.terms}
                    onChange={(e) => setAcceptedCheckboxes((prev) => ({ ...prev, terms: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-accent"
                  />
                  <span className="text-sm text-text">
                    I have read and agree to the{" "}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
                      className="text-accent hover:underline font-medium"
                    >
                      Terms of Service
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedCheckboxes.privacy}
                    onChange={(e) => setAcceptedCheckboxes((prev) => ({ ...prev, privacy: e.target.checked }))}
                    className="mt-1 w-4 h-4 accent-accent"
                  />
                  <span className="text-sm text-text">
                    I have read and agree to the{" "}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
                      className="text-accent hover:underline font-medium"
                    >
                      Privacy Policy
                    </button>
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => signOut()}
                  className="flex-1 px-4 py-3 border border-border rounded-md text-text font-[family-name:--font-ui] text-sm font-medium hover:bg-black/5 active:bg-black/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => allCheckboxesAccepted && acceptTerms()}
                  disabled={!allCheckboxesAccepted}
                  className="flex-1 px-4 py-3 bg-accent text-white rounded-md font-[family-name:--font-ui] text-sm font-medium hover:opacity-85 active:opacity-75 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  I Agree & Continue
                </button>
              </div>
            </div>

            {showTerms && (
              <Modal title="Terms of Service" onClose={() => setShowTerms(false)}>
                <TermsOfService onContactClick={() => { setShowTerms(false); setShowContact(true); }} />
              </Modal>
            )}

            {showPrivacy && (
              <Modal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
                <PrivacyPolicy onContactClick={() => { setShowPrivacy(false); setShowContact(true); }} />
              </Modal>
            )}

            {showContact && <ContactForm onClose={() => setShowContact(false)} userId={user.uid} />}
          </div>
        </div>
      </SignInPromptContext.Provider>
    );
  }

  // Default: render children (both anonymous and fully-authenticated users)
  return (
    <SignInPromptContext.Provider value={{ promptSignIn }}>
      {children}

      {/* Sign-in modal — triggered by promptSignIn() from any component */}
      {!user && showSignInModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setShowSignInModal(false)}>
          <div
            className="bg-bg max-w-[400px] w-full rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <LogoIcon className="w-10 h-10 text-accent mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-text mb-1">GlycemicAI</h2>
              <p className="text-sm text-muted mb-6">{signInReason}</p>

              <button
                onClick={handleSignIn}
                disabled={isSigningIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-border rounded-md hover:bg-black/5 active:bg-black/10 transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-text font-[family-name:--font-ui] font-medium text-sm">
                  {isSigningIn ? "Signing in..." : "Continue with Google"}
                </span>
              </button>

              <button
                onClick={() => setShowSignInModal(false)}
                className="mt-3 text-sm text-muted font-[family-name:--font-ui] hover:text-text transition-colors"
              >
                Maybe later
              </button>

              <p className="text-[11px] text-muted text-center mt-5 font-[family-name:--font-ui]">
                By signing in, you agree to our{" "}
                <button type="button" onClick={() => { setShowSignInModal(false); setShowTerms(true); }} className="text-accent hover:underline">Terms</button>
                {" "}and{" "}
                <button type="button" onClick={() => { setShowSignInModal(false); setShowPrivacy(true); }} className="text-accent hover:underline">Privacy Policy</button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Terms/Privacy modals (accessible from sign-in modal links) */}
      {showTerms && (
        <Modal title="Terms of Service" onClose={() => setShowTerms(false)}>
          <TermsOfService onContactClick={() => { setShowTerms(false); setShowContact(true); }} />
        </Modal>
      )}

      {showPrivacy && (
        <Modal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
          <PrivacyPolicy onContactClick={() => { setShowPrivacy(false); setShowContact(true); }} />
        </Modal>
      )}

      {showContact && <ContactForm onClose={() => setShowContact(false)} userId={user?.uid} />}
    </SignInPromptContext.Provider>
  );
}
